import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { generateSessionId } from '../utils/storage';

export interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  metadata?: {
    guidelines_applied?: number;
    medical_safety_level?: 'standard' | 'elevated' | 'critical';
    requires_escalation?: boolean;
    requires_human_review?: boolean;
    tools_executed?: string[];
    processing_time_ms?: number;
    system_message?: boolean;
    emergency_shutdown?: boolean;
    blocked_access?: boolean;
  };
}

export interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  created_at: Date;
  updated_at: Date;
  user_type: 'patient' | 'staff' | 'visitor';
  context?: Record<string, any>;
  is_escalated?: boolean;
  is_archived?: boolean;
}

interface ChatSessionContextType {
  // Current session
  currentSession: ChatSession | null;
  setCurrentSession: (session: ChatSession | null) => void;
  
  // All sessions
  sessions: ChatSession[];
  setSessions: (sessions: ChatSession[]) => void;
  
  // Session management
  createNewSession: (userType?: 'patient' | 'staff' | 'visitor', context?: Record<string, any>) => ChatSession;
  updateSession: (sessionId: string, updates: Partial<ChatSession>) => void;
  deleteSession: (sessionId: string) => void;
  archiveSession: (sessionId: string) => void;
  
  // Message management
  addMessage: (sessionId: string, message: Omit<ChatMessage, 'id'>) => void;
  updateMessage: (sessionId: string, messageId: string, updates: Partial<ChatMessage>) => void;
  
  // Persistence
  saveSessions: () => void;
  loadSessions: () => void;
  
  // Session selection
  selectSession: (sessionId: string) => void;
  
  // Utility
  generateSessionTitle: (messages: ChatMessage[]) => string;
}

const ChatSessionContext = createContext<ChatSessionContextType | undefined>(undefined);

export const useChatSession = (): ChatSessionContextType => {
  const context = useContext(ChatSessionContext);
  if (!context) {
    throw new Error('useChatSession must be used within a ChatSessionProvider');
  }
  return context;
};

interface ChatSessionProviderProps {
  children: ReactNode;
}

export const ChatSessionProvider: React.FC<ChatSessionProviderProps> = ({ children }) => {
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);

  // Load sessions from localStorage on mount
  useEffect(() => {
    loadSessions();
  }, []);

  // Save sessions to localStorage whenever sessions change
  useEffect(() => {
    saveSessions();
  }, [sessions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove auto-save of current session to prevent infinite loops
  // Sessions are already saved when updated through updateSession

  const createNewSession = (userType: 'patient' | 'staff' | 'visitor' = 'patient', context?: Record<string, any>): ChatSession => {
    const newSession: ChatSession = {
      id: generateSessionId(),
      title: 'Chat Session',
      messages: [],
      created_at: new Date(),
      updated_at: new Date(),
      user_type: userType,
      context,
      is_escalated: false,
      is_archived: false
    };

    setSessions(prev => [newSession, ...prev]);
    setCurrentSession(newSession);
    
    return newSession;
  };

  const updateSession = (sessionId: string, updates: Partial<ChatSession>) => {
    setSessions(prev => prev.map(session => {
      if (session.id === sessionId) {
        const updatedSession = {
          ...session,
          ...updates,
          updated_at: new Date()
        };
        
        // Update current session if it's the one being updated
        if (currentSession?.id === sessionId) {
          setCurrentSession(updatedSession);
        }
        
        return updatedSession;
      }
      return session;
    }));
  };

  const deleteSession = (sessionId: string) => {
    setSessions(prev => prev.filter(session => session.id !== sessionId));
    
    // Clear current session if it was deleted
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
    }
  };

  const archiveSession = (sessionId: string) => {
    updateSession(sessionId, { is_archived: true });
  };

  const addMessage = (sessionId: string, message: Omit<ChatMessage, 'id'>) => {
    console.log('ChatSessionContext: addMessage called with:', { sessionId, message });
    const newMessage: ChatMessage = {
      ...message,
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: message.timestamp || new Date()
    };

    console.log('ChatSessionContext: Created new message:', newMessage);

    setSessions(prev => {
      console.log('ChatSessionContext: Current sessions:', prev.map(s => ({ id: s.id, messagesCount: s.messages.length })));
      const updatedSessions = prev.map(session => {
        if (session.id === sessionId) {
          const updatedMessages = [...session.messages, newMessage];
          const updatedSession = {
            ...session,
            messages: updatedMessages,
            title: session.messages.length === 0 ? generateSessionTitle(updatedMessages) : session.title,
            updated_at: new Date()
          };

          console.log('ChatSessionContext: Updated session:', { id: updatedSession.id, messagesCount: updatedSession.messages.length });

          // Update current session if it's the one being updated
          if (currentSession?.id === sessionId) {
            console.log('ChatSessionContext: Updating current session');
            setCurrentSession(updatedSession);
          }

          return updatedSession;
        }
        return session;
      });
      console.log('ChatSessionContext: Updated sessions:', updatedSessions.map(s => ({ id: s.id, messagesCount: s.messages.length })));
      return updatedSessions;
    });
  };

  const updateMessage = (sessionId: string, messageId: string, updates: Partial<ChatMessage>) => {
    setSessions(prev => prev.map(session => {
      if (session.id === sessionId) {
        const updatedMessages = session.messages.map(msg =>
          msg.id === messageId ? { ...msg, ...updates } : msg
        );
        const updatedSession = {
          ...session,
          messages: updatedMessages,
          updated_at: new Date()
        };

        // Update current session if it's the one being updated
        if (currentSession?.id === sessionId) {
          setCurrentSession(updatedSession);
        }

        return updatedSession;
      }
      return session;
    }));
  };

  const selectSession = (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session) {
      setCurrentSession(session);
    }
  };

  const generateSessionTitle = (messages: ChatMessage[]): string => {
    if (messages.length === 0) return 'Chat Session';
    
    const firstUserMessage = messages.find(msg => msg.sender === 'user');
    if (firstUserMessage) {
      // Take first 50 characters and add ellipsis if longer
      const title = firstUserMessage.content.trim();
      return title.length > 50 ? title.substring(0, 47) + '...' : title;
    }
    
    return 'Chat Session';
  };

  const saveSessions = () => {
    try {
      const sessionsToSave = sessions.filter(session => !session.is_archived);
      localStorage.setItem('ai_chat_sessions', JSON.stringify(sessionsToSave));
      localStorage.setItem('current_session_id', currentSession?.id || '');
    } catch (error) {
      console.error('Failed to save chat sessions:', error);
    }
  };

  const loadSessions = () => {
    try {
      const savedSessions = localStorage.getItem('ai_chat_sessions');
      const currentSessionId = localStorage.getItem('current_session_id');

      if (savedSessions) {
        const parsedSessions: ChatSession[] = JSON.parse(savedSessions).map((session: any) => ({
          ...session,
          created_at: new Date(session.created_at),
          updated_at: new Date(session.updated_at),
          messages: session.messages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }))
        }));

        setSessions(parsedSessions);

        // Restore current session
        if (currentSessionId) {
          const session = parsedSessions.find(s => s.id === currentSessionId);
          if (session) {
            setCurrentSession(session);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load chat sessions:', error);
    }
  };

  const value: ChatSessionContextType = {
    currentSession,
    setCurrentSession,
    sessions,
    setSessions,
    createNewSession,
    updateSession,
    deleteSession,
    archiveSession,
    addMessage,
    updateMessage,
    saveSessions,
    loadSessions,
    selectSession,
    generateSessionTitle
  };

  return (
    <ChatSessionContext.Provider value={value}>
      {children}
    </ChatSessionContext.Provider>
  );
};